/**
 * Datas de calendário — as que o sistema troca com o gateway e imprime em documento.
 *
 * Todas em **AAAA-MM-DD**, sem hora e sem fuso: um vencimento é um dia no calendário de
 * quem paga, não um instante. Guardar `Date` aqui traria o fuso de volta pela porta dos
 * fundos, e um boleto que vence "dia 5" viraria "dia 4 às 21h" para metade do país.
 */

/** Dia do calendário em São Paulo, que é onde a operação e o pagador estão. */
export const FUSO_DA_OPERACAO = 'America/Sao_Paulo';

const FORMATADOR_ISO = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_DA_OPERACAO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Hoje, no fuso da operação.
 *
 * `new Date().toISOString().slice(0, 10)` daria o dia em **UTC** — e das 21h à meia-noite
 * de Brasília o UTC já virou o dia seguinte. Um vencimento mínimo calculado assim recusaria
 * "hoje" para o operador que está trabalhando à noite, sem explicar por quê.
 */
export function hojeNaOperacao(agora: Date = new Date()): string {
  return FORMATADOR_ISO.format(agora);
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface PartesDaData {
  ano: number;
  mes: number;
  dia: number;
}

/** `null` quando não é uma data de calendário válida — "2026-02-31" também é recusada. */
export function partesDaData(iso: string): PartesDaData | null {
  const casou = ISO.exec(iso);
  if (!casou) return null;

  const ano = Number(casou[1]);
  const mes = Number(casou[2]);
  const dia = Number(casou[3]);

  if (mes < 1 || mes > 12) return null;
  if (dia < 1 || dia > diasNoMes(ano, mes)) return null;

  return { ano, mes, dia };
}

export function diasNoMes(ano: number, mes: number): number {
  // Dia 0 do mês seguinte é o último do mês pedido. `Date.UTC` evita o fuso local.
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

function paraIso({ ano, mes, dia }: PartesDaData): string {
  return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** Ordem cronológica sem virar `Date`: em AAAA-MM-DD a ordem alfabética já é a do calendário. */
export function dataEhAnterior(data: string, referencia: string): boolean {
  return data < referencia;
}

/**
 * Soma dias corridos. É a conta dos prazos contratuais — 3 dias para o sinal, 7 para o
 * saldo (Carta, cl. 7.1; Termo, cl. 3.2) —, que contam dia, não mês.
 */
export function somarDias(iso: string, dias: number): string {
  const partes = partesDaData(iso);
  if (!partes) throw new Error(`Data inválida: ${iso}`);

  const data = new Date(Date.UTC(partes.ano, partes.mes - 1, partes.dia + dias));
  return data.toISOString().slice(0, 10);
}

/**
 * Soma meses mantendo o **dia do mês**, encurtando para o último dia quando ele não existe.
 *
 * É a regra do carnê: escolhido o dia 5, todas as parcelas vencem no dia 5. Escolhido o 31,
 * fevereiro não tem 31 — e a parcela vence no dia 28 (ou 29), que é o que o Asaas faz e o
 * que qualquer carnê de papel faz. O mês seguinte volta ao dia 31, porque o dia de
 * referência continua sendo o do **primeiro** vencimento, e não o da parcela anterior:
 * encadear a partir da anterior faria 31/jan → 28/fev → 28/mar, e o vencimento migraria
 * para sempre depois de um único fevereiro.
 */
export function somarMeses(iso: string, meses: number): string {
  const partes = partesDaData(iso);
  if (!partes) throw new Error(`Data inválida: ${iso}`);

  const total = partes.ano * 12 + (partes.mes - 1) + meses;
  const ano = Math.floor(total / 12);
  const mes = (total % 12) + 1;

  return paraIso({ ano, mes, dia: Math.min(partes.dia, diasNoMes(ano, mes)) });
}

/** "05/09/2026" — como a data aparece em tela e em documento. */
export function formatarDataCurta(iso: string): string {
  const partes = partesDaData(iso);
  if (!partes) return iso;
  return `${String(partes.dia).padStart(2, '0')}/${String(partes.mes).padStart(2, '0')}/${partes.ano}`;
}
